'use client';

import React, {useEffect, useMemo, useState} from 'react';
import {ChevronLeft, ChevronRight, X} from "lucide-react";
import Image from "next/image";

interface GalleriaProps {
    photos: { image: string; defaultimage: string }[];
}

const CarGallery: React.FC<GalleriaProps> = ({ photos }) => {
    if (!photos || photos.length === 0) {
        return (
            <div className="bg-white/40 p-2 rounded-lg shadow-md max-lg:shadow-none h-full flex items-center justify-center">
                <div className="relative aspect-auto h-full">
                    <img
                        src={"/autoinarrivo.webp"}
                        alt={`Foto non disponibile`}
                        className="w-full h-full object-cover rounded-lg"
                    />
                </div>
            </div>
        );
    }

    const defaultIndex = useMemo(() => {
        return photos.findIndex(photo => photo.defaultimage === "1") ?? 0;
    }, [photos]);
    const initialIndex = defaultIndex !== -1 ? defaultIndex : 0;
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    const [isModalOpen, setIsModalOpen] = useState(false);


    const visibleThumbnails = useMemo(() => {
        return photos.slice(
            Math.max(0, currentIndex - 2),
            Math.min(photos.length, currentIndex + 3)
        );
    }, [currentIndex, photos]);


    const nextPhoto = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === photos.length - 1 ? 0 : prevIndex + 1
        );
    };

    const prevPhoto = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? photos.length - 1 : prevIndex - 1
        );
    };

    useEffect(() => {
        const preloadImages = () => {
            const img = new window.Image();
            img.src = photos[currentIndex]?.image;
        };
        preloadImages();
    }, [currentIndex, photos]);


    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    return (
        <div className="bg-white/40 p-2 rounded-lg shadow-md max-lg:shadow-none h-full">
            <div className="flex flex-col lg:flex-row gap-4 h-full">
                {/* Galleria principale */}
                <div className="relative flex-grow">
                    <div className="relative aspect-auto h-full" onClick={openModal}>
                        <Image
                            src={photos[currentIndex].image}
                            alt={`Foto ${currentIndex + 1}`}
                            className="w-full h-full object-cover rounded-lg"
                            width={600}
                            height={400}
                            quality={80}
                            priority={true}
                            placeholder="blur"
                            blurDataURL="/autoinarrivo.webp"
                        />

                        {/* Contatore in alto a destra */}
                        <span aria-label={`Foto ${currentIndex + 1} di ${photos.length}`}
                              className="absolute top-2 right-2 bg-white/50 px-3 py-1 lg:hidden rounded-full text-sm">
                            {`${currentIndex + 1}/${photos.length}`}
                        </span>
                    </div>
                    <button
                        onClick={prevPhoto}
                        aria-label="Foto precedente"
                        className="absolute top-1/2 left-4 transform -translate-y-1/2 bg-white text-primary rounded-full cursor-pointer p-2 lg:hidden"
                    >
                        <ChevronLeft/>
                    </button>
                    <button
                        onClick={nextPhoto}
                        aria-label="Foto successiva"
                        className="absolute top-1/2 right-4 transform -translate-y-1/2 bg-white text-primary rounded-full cursor-pointer p-2 lg:hidden"
                    >
                        <ChevronRight/>
                    </button>
                    {/* Sezione con pulsanti galleria e imperfezioni */}
                    <div className="absolute w-full bottom-0 flex justify-between max-lg:hidden">
                        <div className="flex gap-2 p-2">
                            {/*<div className="bg-white p-1 flex gap-x-2 rounded-xl">
                                <button
                                    className="bg-primary text-white px-4 py-2 rounded-lg font-semibold flex-grow"
                                >
                                    Galleria
                                </button>
                            </div>*/}
                        </div>
                        {/* Navigazione tra le immagini */}
                        <div className="flex items-center justify-between p-2 rounded-lg gap-x-2">
                            <button onClick={prevPhoto}
                                    className="bg-white text-primary rounded-full cursor-pointer p-2">
                                <ChevronLeft/>
                            </button>
                            <span
                                className="bg-white/50 px-3 w-20 text-center py-2 rounded-full">{`${currentIndex + 1}/${photos.length}`}</span>
                            <button onClick={nextPhoto} className="bg-white text-primary rounded-full p-2">
                                <ChevronRight/>
                            </button>
                        </div>
                    </div>
                </div>
                {/* Miniature laterali */}
                <div className="flex flex-row lg:flex-col h-full gap-2 gap-y-3 max-lg:hidden">
                    {visibleThumbnails.map((photo, localIndex) => {
                        const globalIndex = photos.indexOf(photo); // Calcola l'indice globale
                        return (
                            <Image
                                key={globalIndex}
                                src={photo.image}
                                alt={`Thumbnail ${globalIndex + 1}`}
                                className={`max-w-[120px] min-w-[120px] !w-[120px] h-[80px] object-cover rounded-lg cursor-pointer transition ${
                                    globalIndex === currentIndex ? "ring-2 ring-blue-500" : "hover:opacity-80"
                                }`}
                                onClick={() => setCurrentIndex(globalIndex)}
                                width={120}
                                height={80}
                                quality={80}
                                loading="lazy"
                            />
                        );
                    })}
                </div>

            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-75 z-[9999999999] flex items-center justify-center">
                    <div className="relative w-full max-w-4xl p-4">
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 text-white z-30 m-3 bg-black bg-opacity-50 p-2 rounded-full"
                        >
                            <X size={24}/>
                        </button>
                        <div className="relative">
                            <Image
                                src={photos[currentIndex].image}
                                alt={`Foto ${currentIndex + 1}`}
                                className="w-full h-auto object-contain"
                                width={800}
                                height={600}
                                quality={90}
                                loading="lazy"
                            />
                        </div>
                        <div className="flex justify-between items-center mt-4">
                            <button onClick={prevPhoto} className="text-white bg-black bg-opacity-50 p-2 rounded-full">
                                <ChevronLeft size={32} />
                            </button>
                            <span className="text-white">{`${currentIndex + 1}/${photos.length}`}</span>
                            <button onClick={nextPhoto} className="text-white bg-black bg-opacity-50 p-2 rounded-full">
                                <ChevronRight size={32} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CarGallery;
